import { uniqueId } from 'lodash';
import i18n from 'i18next';
import {
  AdminProduct,
  AdminProductTypeEnum,
  AdminProductVariant,
  LocaleResponse,
  LocalPropertyValue,
  Material,
  SalesChannel,
  Video,
} from '@cdaas/api-clients/dist/product-service-openapi';

import { addKey, addKeys } from './entities';
import { AdminProductVariantWithKey, ProductTranslationWithKey, SpecItemTranslationWithKey, SpecItemWithKey } from './types';
import { ProductsListItem } from '../features/Products/ProductList/ProductsList';

// TODO - backend endpoint that returns CNET pre-populated product should return
// an object of type AdminProduct according to backend API spec typescript definition
// In AdminProduct
// - each main spec should have an id
// - each translation of each main spec should have an id
// - each image should have an id
// - each variant should have an id
// ...
// However backend currently does not set dummy values to these id-fields and hence they are set here (id's with value 0)
export const getAdminProductFromPopulatedProduct = (product: AdminProduct): AdminProduct => {
  return {
    ...product,
    id: 0,
    main_specs: product.main_specs?.map(spec => ({
      ...addKey(spec),
      id: 0,
      translations: addKeys(spec.translations).map(translation => ({ ...translation, id: 0 })),
    })),
    images: product.images ? product.images.map(image => ({ ...image, id: 0 })) : undefined,
    translations: addKeys(product.translations).map(translation => ({ ...translation, id: 0 })),
    variants: product.variants?.map(variant => ({
      ...addKey(variant),
      id: 0,
      prices: addKeys(variant.prices),
    })),
  };
};

export const getInitialAdminProduct = (brand = '', sku = ''): AdminProduct & { variants: AdminProductVariantWithKey[] } => ({
  id: 0,
  type: AdminProductTypeEnum.Hardware, // default to hardware
  name: '',
  description: '',
  brand,
  subcategory_ids: [],
  box_content: [],
  key_selling_points: [],
  variants: [
    {
      id: 0,
      sku,
      key: uniqueId(),
      country_acquisition_prices: [],
      finance_product_ids: [],
      sales_channel_acquisition_prices: [],
    },
  ],
});

export const getInitialProductTranslation = (localeId: LocaleResponse['id']): ProductTranslationWithKey => ({
  id: 0,
  key: uniqueId(),
  description: '',
  locale_id: localeId,
  name: '',
  key_selling_points: [],
  box_content: [],
});

export const getInitialSpecTranslation = (localeId: LocaleResponse['id'], linesCount?: number): SpecItemTranslationWithKey => {
  return {
    id: 0,
    key: uniqueId(),
    lines: linesCount ? new Array(linesCount).fill('') : [],
    locale_id: localeId,
    name: '',
  };
};

export const getInitialSpec = (localeIds: number[]): SpecItemWithKey => {
  return {
    id: 0,
    key: uniqueId(),
    name: '',
    lines: [],
    translations: localeIds.map(localeId => getInitialSpecTranslation(localeId)),
  };
};

export const getInitialVariant = (): AdminProductVariantWithKey => {
  return {
    id: 0,
    key: uniqueId(),
    prices: [],
    sku: '',
    property_value_ids: [],
    country_acquisition_prices: [],
    finance_product_ids: [],
    sales_channel_acquisition_prices: [],
  };
};

export const getInitialVideo = (localeId: LocaleResponse['id']): Video => {
  return {
    id: 0,
    locale_id: localeId,
    url: '',
  };
};

export const getInitialSalesChannel = (): SalesChannel => ({
  id: 0,
  country_id: 0,
  locale_ids: [],
  name: '',
  product_variations_count: 0,
  settings: {
    id: 0,
    show_product_images: false,
    show_product_description: false,
    show_customer_reviews: false,
    show_product_key_selling_points: false,
    show_product_videos: false,
    show_technical_specification: false,
  },
});

/**
 * Availibility mapping
 */

interface IAvailabilityMapping {
  [key: number]: {
    color: string;
    text: string;
  };
}
export const availabilityMapping = (): IAvailabilityMapping => {
  enum MockAvailabilityEnum {
    Available,
    NotAvailable,
    LimitedAvailability,
    OutOfStock,
  }
  const i18nNamespace = 'productsscreen';
  return {
    [MockAvailabilityEnum.Available]: { color: 'green', text: i18n.t(`${i18nNamespace}:product_availabilities.available`) },
    [MockAvailabilityEnum.NotAvailable]: { color: 'red', text: i18n.t(`${i18nNamespace}:product_availabilities.not_available`) },
    [MockAvailabilityEnum.LimitedAvailability]: { color: 'orange', text: i18n.t(`${i18nNamespace}:product_availabilities.limited`) },
    [MockAvailabilityEnum.OutOfStock]: { color: 'black', text: i18n.t(`${i18nNamespace}:product_availabilities.out_of_stock`) },
  };
};

// Helper for compare two products
export const compareProducts = (product: ProductsListItem, existingProduct: ProductsListItem): boolean => {
  if (!product.parent_id && !existingProduct.parent_id) {
    return product.id === existingProduct.id;
  }
  return product.parent_id === existingProduct.parent_id && product.id === existingProduct.id;
};

/**
 * Get count of SKUs by item list
 * Handle whole products
 * @param selectedItems ProductsListItem[]
 */
export const getSKUsCountByListItems = (selectedItems: ProductsListItem[]): number => {
  if (!selectedItems || selectedItems.length === 0) {
    return 0;
  }

  return selectedItems.filter(product => product.sku).length;
};

export const combineVariantName = (properties: string[]) => properties.join(', ') || '';

export const getVariantMaterials = (countryId: number, variant?: AdminProductVariant) =>
  variant?.materials?.filter((material: Material) => material.country_id === countryId) || [];

export const getVariantsPropertyValues = (variants?: AdminProductVariant[], property_values?: LocalPropertyValue[]) => {
  if (!(variants && property_values)) {
    return [];
  }
  const propertyValues: Record<number, LocalPropertyValue> = {};
  property_values.forEach(propertyValue => {
    if (propertyValue.id) {
      propertyValues[propertyValue.id] = propertyValue;
    }
  });

  const variantProperties: Record<AdminProductVariant['id'], string[]> = {};
  variants.forEach(variant => {
    if (variant.id) {
      variantProperties[variant.id] =
        variant.property_value_ids?.map(valueId => {
          return propertyValues[valueId].value || '';
        }) || [];
    }
  });
  return variantProperties;
};
